/**
 * The geometric and harmonic means, and the average growth rate that the geometric
 * mean of growth factors gives.
 *
 * The geometric mean is computed on the log scale — exp(mean(ln x)) — rather than as
 * the n-th root of a product, which overflows for a few hundred ordinary values.
 */

import { mean } from "@/lib/stats/descriptive";

/** NaN unless every value is positive. */
export function geometricMean(values: readonly number[]): number {
  if (values.length === 0 || values.some((value) => !(value > 0))) {
    return Number.NaN;
  }
  return Math.exp(mean(values.map(Math.log)));
}

/** n / Σ(1/x). NaN unless every value is positive. */
export function harmonicMean(values: readonly number[]): number {
  if (values.length === 0 || values.some((value) => !(value > 0))) {
    return Number.NaN;
  }
  let reciprocals = 0;
  for (const value of values) {
    reciprocals += 1 / value;
  }
  return values.length / reciprocals;
}

export interface MeansReport {
  n: number;
  arithmetic: number;
  geometric: number;
  harmonic: number;
  /** Σ ln x. */
  sumLog: number;
  /** (Σ ln x) / n. */
  meanLog: number;
  /** Σ 1/x. */
  sumReciprocal: number;
}

export function meansReport(values: readonly number[]): MeansReport | null {
  const n = values.length;
  if (n === 0 || values.some((value) => !(value > 0))) {
    return null;
  }
  let sumLog = 0;
  let sumReciprocal = 0;
  for (const value of values) {
    sumLog += Math.log(value);
    sumReciprocal += 1 / value;
  }
  return {
    n,
    arithmetic: mean(values),
    geometric: Math.exp(sumLog / n),
    harmonic: n / sumReciprocal,
    sumLog,
    meanLog: sumLog / n,
    sumReciprocal,
  };
}

export interface GrowthReport {
  n: number;
  /** The percentage changes as entered, e.g. 10 for +10 %. */
  changes: number[];
  /** 1 + change / 100 for each period. */
  factors: number[];
  /** Π factors: the overall multiplier across every period. */
  product: number;
  /** (Π factors)^(1/n): the average per-period multiplier. */
  averageFactor: number;
  /** averageFactor − 1, as a proportion (0.07 = 7 % per period). */
  averageRate: number;
  /** The arithmetic mean of the changes, as a proportion — the tempting wrong answer. */
  arithmeticRate: number;
}

/**
 * The constant per-period rate that compounds to the same overall change. Null when a
 * change is −100 % or worse, which leaves nothing to grow.
 */
export function growthReport(changes: readonly number[]): GrowthReport | null {
  const n = changes.length;
  if (n === 0) {
    return null;
  }
  const factors = changes.map((change) => 1 + change / 100);
  if (factors.some((factor) => !(factor > 0))) {
    return null;
  }
  let sumLog = 0;
  for (const factor of factors) {
    sumLog += Math.log(factor);
  }
  const averageFactor = Math.exp(sumLog / n);
  return {
    n,
    changes: [...changes],
    factors,
    product: Math.exp(sumLog),
    averageFactor,
    averageRate: averageFactor - 1,
    arithmeticRate: mean(changes) / 100,
  };
}
